import type { NostrEvent } from '@nostrify/nostrify';
import { ALSO_FILED_UNDER, COLLECTIONS, type Facet } from '@/lib/taxonomy';
import { deriveNoteTags, resolveSlug, termBySlug } from '@/lib/noteTags';
import { hideHashtags } from '@/lib/noteUtils';

/** Worksheet codes are collection assignments, not assertions about a record's format or provenance. */
export const FILING_CODES: Readonly<Record<string, string>> = {
  'wtc-collapse': 'W', planes: 'P', media: 'M', testimony: 'T', legal: 'L',
  'west-asia': 'A', pager: 'I', bitcoin: 'B', resources: 'R',
};

/**
 * Curator's 219-record worksheet: 186 marked records, 33 blanks left unchanged.
 * Nonblank entries REPLACE automatic collection membership, not subject/format tags.
 * Keys are full event IDs; duplicate titles remain distinct records. 0082 was
 * confirmed as M,L,T; 0211's trailing comma was removed. No Nostr events are edited.
 */
export const RECORD_FILING_CODES: Readonly<Record<string, string>> = {
  'ceff7f2e46fd1536f2b4891440f1052fd9c9b820cc8e22c0604564d1f93fb670': 'M,T',
  '6f153f09397414a786436cb225e0dfe5ca0f0e6984d5f47df0a6a7b3584d7dfa': 'M',
  '85956b46b12313d75704d370e8623a88e28ca14febf23e6d99aac2e43e25957b': 'M',
  'fd4b64345cf82d481e38d3f06c0edff903119c23f77518f8b3c06d7284468f79': 'M,L',
  'd5e4a216ce2816de3c15e0d581c329b4f7264e413559c4653511ccbff36a4c51': 'M,L',
  'e6098e942df9fb36472491696066575d61916df415fa0947134a62792ce99216': 'M,L',
  '7e8f6b89f31703660b33900cbd2b8ee35a6a40b1587f09c8dd9fe0b0f58a5364': 'M',
  'ddcc1bbd2de75354e14fe266e194b4304185f3c8051041dda3ec5c9f9f8174c2': 'M',
  '49dbcceed0ea30b045870f0f41d6d018e282f063c8034de925ca14146b5b6f12': 'M',
  '330af4c0261867c6ed7eed780762c1ad6df7d3e8681bd3dc41493be6c91ecd7f': 'M',
  'e1e46b0d98f1cd163a4d65eb1a7117a847ca4ef14939d0f0f7165c940220686a': 'I',
  'f00e3e5a8a718d27c4645be5de6ad2dd0f0adc0262311d29405f50455260ea1d': 'M',
  '9010e3aaa1e0e780ea2dd657d26a67f496cd08b864ba026ea273428a27d4907e': 'L',
  '616f6406df2671fadea39f5c462ad218418454e8e6b02960e67a283893b13793': 'M,L',
  'c064968aa549f8ffa520b103247b1eecb1d8177c5396a4e8a3e08925594ed678': 'M,A',
  '4808d3d3e816d99b95309547e81f1693255ff7b1651a03edee6c06054689dd49': 'M',
  '09fb472d18caaafbaf3ddf7c50395b9ab9be3c05dee2ce4a04147f2ab2038b78': 'M',
  '747a2f5897aaf25a0e0b540f6abd5ea2c37980728caa5e1e77017bf192bab5b2': 'M',
  'c119694d4a666634ee1d49696d527647afe408e9190967d13084a09a5f9ae39b': 'M,A',
  '4c96bd97f4dda6aa8535e052af7125fb198d331d18eec28add5339a922b3c4de': 'M',
  '0a35e9f762278e01a2d189a4f475a5580fa763bba8b5f9f7e30d4642e92b43c4': 'M,A',
  '2d8d390e6cba8bb945b4b612351dd3c0c2be557ff12f71db90899cf5abaab0fb': 'M',
  'c9814e5c520e3df675b6b3a8278e4342c4a26bfd9723956b6961215cefcf436e': 'M',
  '2a5d5de126f70c8a72ac22687c3e3a0525d333a9533d3fd2c8481bc42f44b1eb': 'L',
  'd15a4202c10c776d0711a3ee6f1c553a3e3e334a989cdf44e56a66f15d1be02a': 'L',
  '5851c619ee2a97f0c81d897a708928a38afde2b1586735790b0ada6688c9b4b3': 'L',
  '9db11ebd524fe49a9aa2e4c1fa5388782173d03b6f8aa1e5b74866d2cc0c033e': 'M,A',
  '2ff2b6ada6d559ef046c68e937f440a5329ada808f7f72c6d9c6ab415cf716da': 'L',
  '6fdef9049d0e848be91dea86ba6ba9331ec8f9d1c9931f511ef067d5706b8ded': 'T',
  'ccc3fdb54fb09750e1e45e4fd9ed5e1e9857dcd4d088c592c4ed2558da8dd4e2': 'M',
  '36ccf693514c1ca71fd8a19472938534575f7eec6ab36a238188a65a7ddba344': 'M',
  '1823eb27b30d767adeb397dad52236b72789d53dc388ef7b2c13715a0b755ff5': 'M,T,L',
  'e9180e0c642a053d59edc865a6244d62973fac4f73273b92ac00e943a1103406': 'M,P',
  'effa7e2e7e1e1bc64aaa0257eaaf5abc2bf7cdc44134ed819d60bd6a1af0c086': 'M',
  'ddf5ca280798ec4993f4dac12731f3d617109a222a979533a9bf7c3c39e62031': 'I',
  '338cea19dbeaa297f3cbf8e8fa5be03dc22fca27f8cb01576ad2dc7c6a1b5350': 'I,A',
  '80b96a15831821c56f1aed19992716e09893ea0cef0000095f9c9d2a4a9d9e00': 'I,A',
  '5b86949b0e7a41df0ad923093404fb94440b21e5a19b153e82f53676a03caf6e': 'I',
  'c00d6ea26bd763b81840e4189198514d62de923bb3baedfc382dfa0edfbb320a': 'M',
  '2961d3274683a32b1685c5ef1f007d93916eb5f373699521b554d9aff3bd1108': 'T',
  '8795c7da15db1df9ab6e52d5eefae0f8c296f5f25f7819c2ba7b784f74d317bb': 'M',
  '5d880f19fd896b715ebe5dd5cf1b767abe6acfb3605ba4038792648cd167941c': 'L',
  '08d26497cc66f3a3741849c1f552bd328602405e2698e414386d414004807235': 'M,A,T',
  '100b6b706aa1df6e495896e3fec43f15c37db90dae964572437df6dd390364c7': 'M,T',
  '5d8dde873475c41c073156e19c2efcf6dea8e5e68ddc53edebb169341b357313': 'M',
  '826cba840b974d0c1765a919024c17bf253a05c3bbdae092f014cc7dbdb546d5': 'W',
  '24da8988b452024323c1c727036733e0274136d2f82f12bcdeb705d828c9a764': 'M,T',
  '1faf7539b7a77bda74be2611f3a7d7861cbe45e3f974133022f3a740e111c402': 'M,L',
  'ba032bc7ddb31a8b53712376f47e66b54972b57b3d0d39a0492b2400275f2592': 'M,A',
  '53b3d4c2ce10de4400871420b34510cc2d4867ccb223ceca7b92908a71532e99': 'M,T,A',
  '2c55be540c18637c48f107ea902f8279170f4285313dee689b77b515eca3373c': 'L',
  'af57cc8f0057700628b17b0c3422643dcfca5b909a9c9c702f76260e9727ca95': 'M,A',
  '53e215b558ea079a6f7e730a403c6f0c020464aa5145353221a9b32a1b0cd21b': 'A',
  '2879a914f5a76b50df30a0eb8ca68e57470ef1f30b828eb1291b9a87bc5c48e1': 'M,L,T',
  'd8199f5cd565e6dccaaa1c857cd1ea5e9b6975a9ab8f082becb932db81e0e6f7': 'M,L',
  '0182be97f41060b9dc3f5f6199b6f9377eca6efacb3beb5899b8ed613689d005': 'M,L',
  '4a7092319ee85cff613738a3e68f4b9378ffd40ec717f311e3b036c34e3a4cac': 'A',
  '6a403db7166f9dfb7182db343f4b46e56f871daf6138c11d306df132102fc285': 'M',
  'd6d97152d9c8647f609eb6719e57c3e68c109da5fff4253679ad00c6b32a598b': 'M,A',
  '17c278c4aa8b8b815854be4a33c589f522903ec3775682f7dfe49dbd9e2d7bfd': 'I',
  '061ce01eaa67ee4d95542fc7108389190bc9fde8c932cb00bbdb4b928f3673b2': 'I,A',
  '31757fdfdb8f350b675a3a43a0ba0eb60fabc4239ea3272fdad2e77ff6dedc57': 'I,A',
  'f419cb28b2abc48ce14b96808d676d6fea8731d5669fcf960fb1117f2431e47b': 'I,A',
  'cbca6b5f99df7720d2a32f1874f357ece7bdd9c006bfab6c8ae4e442eb354e16': 'I',
  'b716e68832ccd07769528a7de446c95a76d4e28a41cb7719aa71314772627ff3': 'I',
  'b7cc9b11c664a2b6a076dc9afcba445fd13bc15f15bbfe2dbeaaccb54825b76c': 'M,A',
  '88a548cbad51eaa214de769e893df152ac9cf26be4ce7df0dcae677f54917489': 'M,T',
  'b524f6c5255e19358ff32c1532d6ffe348ed2f8ac3c727521b3bba15ce30a19c': 'I',
  '35cdb8d2249f01fb55925df1d20c8e6344c65430f8edc8d4a06a00a3a4de8c5f': 'M,L',
  '44370ffe699773f38d0d08e2dfb91b1bf24993de8bc6a9689384b5e7d9c76c96': 'M',
  '2bf94ee468ce21111be31d5a0860ef06b09a107918ff13484d6e96e8fbbe4d42': 'M',
  '8bf508ed0c0ce083b8b9970a1f487673cb690aa2076f48610e32038dc5fb92ee': 'P',
  'a4afe6ff447c16d4c239acce9ce4ffe5d917246d8eddb2b5eed7a7293fbabce9': 'M,L',
  '3300d14c4d36c681bcee8c4f420344548572784de7dc70cf6843192f59da18bf': 'M,L',
  'd9e9a97c04f00722a8d030e35a0ddc4176cf6f3db0a6c41919a79d49367e6b88': 'M',
  '59261067c4a5e36f4337f3ae2b2b1441464c8a0d53d784e1b79d00a8ad7bfc12': 'L',
  '5a57820b985e92fa3045c4b1d7374a43e1b34f311f3f1753c5c80a3b6d75ad1f': 'M,W',
  'cb2c1f147df4d73deddc887bee7e075def5e58b4f1af5e1b1d8b31059675e90e': 'T',
  'c063bbea4c0db67fbd0f4a7881d1a1df89c2fb38802421de20f100896ad3a479': 'M,L',
  'ae37a0c6daf04d78121e96ed474bab0c927c6052429d49dd3c9068eb4d0a2e93': 'L',
  '7871eac949222419569d155f2ff6b4b0876f1aef48be7ad06023fe3d1bea77ec': 'M',
  'b805fe6d16342afc77597a22b75dec1901e3b5afc3dcb8107e050baf0b6c7563': 'M',
  'd154f834ec6dd6eb14df3adf6f2e8fc23323aebb6cd45b81b56714e26ba365ed': 'L',
  '01cccd2b525f6d2bcefa594927aebb6e9a378441154f331688031499571ff1b1': 'M,L',
  '43da773b8a61fbc589c3756644a4bc35031c643c27665e335d00aec1a0390281': 'M,L',
  '4edde05b2a520b62d3f85cf428fe297f457b077539ac7f9c83e3eb706ecd248d': 'M,T',
  '8f48e4511ceb30f5a07fa3a9c87a8355bde6b65be4cb3768db7de690aa945336': 'L',
  '99f6fd7aeabe1926e66edb4f36e0c87c9abbcd3cb7c5ca78fab50653c37ee540': 'M',
  '721ce5ab778d9d4dc72cb63632c16b91879aa1799c1f063c696f8d0156c19449': 'B',
  '1aabd61c8fd90bf7cd02e39fbf95b9b7265f74df774514789a2179ae075dbf5a': 'L',
  '635e54169e6dc111a45bca1c1ae2e319fa553f9a20153309029efaff4e60b4f2': 'L',
  '76cce7dad20f91057fea9682564c3d2a35b36493c6f6b471debd5f18313812d8': 'W,L',
  'f6a3e3254b9f035dbe6a7b11b523bd2fb048523caa14bde2e1a9cfa1ea462a82': 'M,P',
  'c209c2a5100ae3b801e031e03cf22ecb24f191bbb2a6cf15e74181a08bd0d90d': 'L',
  'c1414eae99c54c7b286d82235a209e51e0ecf02567fb14080b3e6e4869566ec9': 'W,L',
  '437ae24bd1495ff7c32e6695d3cdcbb0794e08aa8199e9bc8f10c9c366561c17': 'W,L',
  '4e16be0406b72c6c991d343c732abf2f242944362e42937b42790298d58eb3ad': 'M,L',
  'ab6212c1c671d4e1e6632b0743c959f10a5e52dfeafa3a4bde3a6a0b29c2e6df': 'M,L',
  '0363ef1ab5e16eb5b86d9ee2c9d5de42479a2de5315076902414055023205b58': 'M,L',
  '52c2d35e0dd28ec32741f27d38efa8ca7bf7fb38ad869d4dcfe49de8dc08df86': 'M,T',
  'b38c04c5c96f54fefb461fad67e219351be2128221fc8a53a5606fa3330a0479': 'M,L',
  '6dc24f9e2c47f372d3dabe52f1e563fd02fdbdcb47a53a5e0570a309ca28165f': 'M,L',
  'fa623b18c7a847d35000b1f0d427cfb2fd7187cc566d3e8ab1af7067b0910f5d': 'M,L',
  '67e0ab15ec16f86cd631021f7e0a8999f4c9e72ee1c8855cbf01e3a49f4ccde5': 'L',
  '7bd607daee15240c11357447c6955e78b5cb9fe171c803382c6886062f9f576d': 'M,A',
  '2d936bcf08bc1202d8c78ae0143e31ce22fc822dc8049941da361aa7de28feb3': 'M,A',
  'efd0c53f7eb2c45535580cab85e4b4c0b4a4622a43451cc3678232e9bbee9186': 'M,A,L',
  'a3f6428bac3b9dfac755e9d1261160c08775c57cfac4a82ef82036239802b458': 'M,T',
  '126e7d0bdcd614fec9e4e17445466a0d35bb11a6e8c22e83e41148ded91c0df5': 'M,T',
  '579fafc870848fa8065b3a7b3699f2b1288754019e0a398a832aa85cbbc44260': 'M,T',
  '21b38fbf960c0a80eb5efd2bb1518af559744c604f4c67c6f399f8e25c44034e': 'M,T',
  'ee432901e69c9e89733bb4b87a0c1d99cd1d2fbebb49c36aa0977473a9f38d9b': 'M,L,A',
  '1e463b4f8e62b7480b5430d94d31794c73d7435b9ea29ce9e248e2a2ccc8bd1b': 'M,A',
  'a9e7a5d7752eefda4e8b5d313eb497203cb2e34c61e959bacb4428dca490fc93': 'M,A',
  'b8841b679c03b98d643926ebdeb306a599a77a24778a54874198136cbfb14b18': 'M,A',
  '116d59a9d4c5df46aa7b768963ac998277074b1ff355a082f7ff78b51345fabe': 'M,A',
  'd4d044bd1de6e4a5e5a29c9455bb042da4ddf6736979217b656f7f156582fdbe': 'M,A',
  'd11b15d684043212e898966c619d345e7d6e2f836bf1b8e7d320b763c9855674': 'B',
  '02a304617975ef4d1bd8af1901a3c8ba7bca69c96772280e4bf2d19a8239ce6a': 'B',
  'b554fd536169b967a41693fe6b2466d6101c68bd1bbb7c540194611929ce0b2f': 'B',
  'be621f24659989a8220a2526d303e9b27c1fc907b959dc4b22b2b22317e72a34': 'B',
  'c1515465df6d19f653b6c7592857480fe6da526c0ea78052c9507fd218c097a4': 'B',
  '7d47f8c598702beb88c9e6555d7519a56f7a6dc2eb170afd23cb384540d25874': 'M,L,A',
  '42f8268f4e7535990e4c9aec9e6c5936a2bd83b492b44dcb5d28b72fa7a5557f': 'L',
  '2ebac27127ca99839f52ce2e0cd9f18e583ce84efb621cf14c17429f2f9a5be3': 'M',
  'aefe95bf2b825c165f038663cf6b4cacc236c76ae9afb1b9abe72bb6956422a2': 'W,L',
  'ebafa61f54d1bfc3fd1c244a221de778ded6d70afd4152f1c0074777724e7fc7': 'M,L',
  'aad4b9328bbe537998365072ec5ed4a1506fabc430e4739f253cc171439191b8': 'M,A',
  '0ba20824f1b1514ea961a64029577cbb481502cb8e29ad37178062c351b018f6': 'L,A',
  '7f34afef53ee7a794de138932461c6d82b2fdc184fa78fe4345dade9ca96202a': 'L,A',
  '9eef08ea2a189b5b1571fb5b765f3bb86fce8ff414a91e90eab73742d11c5efc': 'M',
  'de97917a7507c4c7f3c8f0aff6cf2954eef075b89c8453d12a14837bb3eede41': 'M',
  'c5b16b66b7d05ddea7296dd98b694751382faa2ed9ac16c4f321e19a0caf9127': 'L',
  '976d26c4e7378a46bba9f9b2c712efd9da072620e46cd9d2bd872d089c27d463': 'M',
  '999ff17570a0145cde331bc07eaf771ce28734cd82fa7a8081e5e13a59221d2c': 'M',
  '993d3dfcda91c803c4d9cbc76fdf57c7f43c239424752956df3ad6f6e267e24f': 'M',
  '51108abe9b12db1ae75f4a1f0ca7e1c4271297ad98d84f389b665d656caffd26': 'M',
  '06a62536928335283a6af49ef7b04092baa5857235b281fb925e2d385c51d49d': 'L',
  'df1b60266ae682bc0323553dd38ffa8412e550e571ec27ce6ec0af7d67fef2c0': 'T,L',
  '7a289d3ce50bf7f4d87e788d5f842493148347010206321763d40843283b6c00': 'M',
  'e460acb1a91538d284024019676710aa922ba12502986f6de390e1660a24e98a': 'M',
  '61e4ccafe37ffb9abe9b8e5a9165bc53d7ce81068d16edc8f5b9941ffb99cc5c': 'W,L',
  'e22803210c2a257dcd5d293e27407dde92ac3bef1b889dfbcd057a1a1ff838c2': 'T',
  'dbc89843052f5f5af9aa6830bbd402cd34408640ea87334f71d9f4d7fc8c00fe': 'T',
  'ceb464a4af0168eddae6bf220dda17a2b55ee998c84d4e1c7b2d408432a5ffbe': 'M',
  '81c10c421836b91d81d79db3f49e007e60b35282aa88ace345e097f4c5479661': 'T,L',
  '5f446187d70d565e97e9b90c140f02dc4ab0ae365c16895d79c5b670cd205d72': 'W',
  'dc6b295a263eac917927686209b46908ee2e3960ee3a02d71dce01b2c0902673': 'M,L,A',
  '2d8c99a446ba1c4332bbc3c9354dc89ea60f24d530b9798f386d53cd2785cdf3': 'W',
  'c51bf1647da21c93162f9d66e476766ff9e81d7883d15bce73ca88d9cb2d6bc1': 'M,L,A',
  'f4f19f9b7cc47bc797043c3dce2f61960f95442c13771e7e39fd50e2e2060d3a': 'M',
  '8f0f1552133547a8c3e0c36e247cedd750d0ff716ce6c9dc99559ae6f2251f15': 'M',
  '842c2b16344457984f2739b0e5e37ae36572cc6e5fc6f5760c43eb69ad04ac40': 'M',
  '11b927becb22b6f6c8c2dd357de10d1e7ba6608081a2226b30336aff844b79b0': 'M,I',
  '89f387fa5713eafea9d9dae9cb725d4062ccd651c53d667a27dd056f8c083b3d': 'M,B',
  '9b6102fe0620afc9f674d316ef525d5ec5edfa9aee015851d74ded5a15b09fb0': 'M,I,B',
  '9f9cdbbcc7fca089ac1c4fc6a020090f722f4cd368dc81c61bcdda4de0c39f0f': 'M,I,B',
  '1f823eba2c93329411e4c14b48bcf309313d10cd2319e8a7cf8b99ede5de9cee': 'M',
  '7226d160054d65532e1a8ce7f74cfdb55ad8ec0eca6521a3de16d5e965023db0': 'M',
  '0ae937453dafb45d05ada10f39eb385d8c0a9268586a249a58b0ef3ba0f0a68c': 'M',
  '2481ccb96599a66cd8cc4507c086d515db5ca9e224aba0eae202f6854c4ead82': 'M',
  '4bae71b03dc233120339503c18fd592d623bb624b6c09860e0a3937b02b0eea7': 'M',
  'c984ecf3c784db08dec8c7ef6e0c4aa63e155da83c54fb3dbbde3222ba2e276e': 'B',
  '506aa24ddf02b47e0d795ce21216126212d247c10f7fafb8e061c1dc63cf403f': 'T',
  '41fcbe3d8c0b7f5c0e649fb4bcd6da6f2ad45caa3aa0ad7cea7761635c3f1800': 'M,A',
  'ac38b78a3c09c50e1ea4cff4853b1e9fb6d747a41ca3ffe3a0e60d71fcac1572': 'W,T',
  '64705d441caafd61fa23acdef3b3da50ccf2010cd2ba45cc58883fe773ec780a': 'A',
  '6027f92b4e3fd1a4f0b2b34d4d7f38ad2dd91c6aa0c2e033482e588d8e80595b': 'L',
  '5ff5403c9b438e8acca55584f5dcdfa4931d376d24322f7c554ff018e789d527': 'W',
  '0a3da27647fd51699f65cc512860a0307d2b98530febd078e1b1ad01cd30a1d1': 'T',
  '98d0a4790b57cf09643457f467261aa5a9ae8034c8e368d9d470b8ee2b814e03': 'T',
  '8c6d91cc43d7ef7845e3e4a391be328472a3f1450196ac742068f6242ff9373b': 'M,B',
  'c0eb3ca6aba587de6ddbed65f0e929d471796b48b5e6b2529ea25e6a15197237': 'P',
  '77196b7b04cf9f423008c7858a4d964d0eff54fce23094b851164c19b54115e6': 'W',
  '37c39ba1e6f0d14a65ca30eb7bad7e7e4596a3b67c1a549d326d746b746ab4b8': 'W',
  '87715f071475b745a18b737b13a9a714b6832c9fb674eeaa6b3ede2dfba22de3': 'W',
  'b14b00503059bf6b129b28fccfb47eeab02c2fc78b45e592161efa7cc6b02d84': 'W',
  '4fa90dde22c9468d384aeb941be9429e85901b1362a87a76905a8352bb10a311': 'W',
  '3375f0842b462c5a2408274a2aa116d9ab9e54b85bdbbf3ab20bbb87354dddd0': 'W',
  '0965431a326c8d8369ccb27606b852129ba27b9b2ae41c4c05697966d2c30c76': 'W',
  'cb8931165f3a3c20826dfe8b80ea2f77a3210fc1debb8fbf2a71be090a3be995': 'W',
  'f03c6d50de60d38aea8416dd97b17e19b07a54709293174c028fa16ca62cef94': 'W',
  '38fe165dffd47c3f29b9b3c82802ef1937f6e37d6ae1e15ffa585beff41a8f9f': 'W',
  'ea4aec7338a93f39827ab351a42dc396ebd95a3420af02ca582bf110778455f8': 'W',
  '24dd576cd126573472639583e406231eaab3fe910e8bf8affcfda4bd0b908f16': 'M',
  '2dde82719b8372eebe4c73871d341cf6925e2e3df5d78773f9e29401de95a2a1': 'I',
};

/**
 * Archive filtering, in the standard faceted-search shape:
 *   - terms in the SAME facet are alternatives (OR)   e.g. Press OR Broadcast
 *   - terms in DIFFERENT facets narrow each other (AND) e.g. Press AND Palestine
 *   - a collection is a saved OR-group of terms (what the Directory links to)
 *   - free text must match every word
 *
 * State lives in the URL (`?t=…&t=…&c=…&q=…`) so every view is linkable.
 * The old `?tag=` parameter is still read, and legacy slugs are mapped on.
 */
export interface ArchiveFilters {
  terms: string[];
  collection: string | null;
  query: string;
}

export const EMPTY_FILTERS: ArchiveFilters = { terms: [], collection: null, query: '' };

export function hasFilters(f: ArchiveFilters): boolean {
  return f.terms.length > 0 || f.collection !== null || f.query.trim().length > 0;
}

export function activeFilterCount(f: ArchiveFilters): number {
  return f.terms.length + (f.collection ? 1 : 0) + (f.query.trim() ? 1 : 0);
}

export function parseFilters(params: URLSearchParams): ArchiveFilters {
  const raw = [...params.getAll('t'), ...params.getAll('tag')];
  const terms: string[] = [];
  for (const r of raw) {
    const slug = resolveSlug(r);
    if (slug && !terms.includes(slug)) terms.push(slug);
  }
  const c = params.get('c');
  const collection = COLLECTIONS.some((x) => x.id === c) ? c : null;
  // Kept exactly as typed: the search box is controlled by this value, so
  // trimming here would eat the space between two words as it is typed.
  return { terms, collection, query: params.get('q') ?? '' };
}

/** Write filters into a copy of `params`, leaving unrelated parameters alone. */
export function writeFilters(params: URLSearchParams, f: ArchiveFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('t');
  next.delete('tag');
  next.delete('c');
  next.delete('q');
  for (const t of f.terms) next.append('t', t);
  if (f.collection) next.set('c', f.collection);
  if (f.query) next.set('q', f.query);
  return next;
}

/** A stable string for a filter state (used to key remembered scroll depth). */
export function filterKey(f: ArchiveFilters): string {
  if (!hasFilters(f)) return '_all';
  return [...f.terms].sort().join(',') + `|${f.collection ?? ''}|${f.query.trim().toLowerCase()}`;
}

export interface IndexedNote {
  note: NostrEvent;
  slugs: Set<string>;
  /** Lower-cased prose + term labels, for free-text search. */
  haystack: string;
}

/**
 * Search text is compared in a plain, punctuation-free form so spelling
 * variants meet: "WTC-7", "WTC 7" and "wtc7" all become "wtc 7"; "9/11" becomes
 * "9 11"; accents are dropped.
 */
export function normalizeSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/([a-z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-z])/g, '$1 $2')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Other ways a normalized search word is written in the records. */
const WORD_VARIANTS: Readonly<Record<string, ReadonlyArray<string>>> = {
  // People type "911"; the records say "9/11" (normalized: "9 11").
  '911': ['9 11'],
};

/** Every phrase a typed word may match: itself, plus any known variant. */
function alternatives(norm: string): string[] {
  return [norm, ...(WORD_VARIANTS[norm] ?? [])];
}

export function indexNotes(notes: NostrEvent[]): IndexedNote[] {
  return notes.map((note) => {
    const tags = deriveNoteTags(note);
    // Hashtags are hidden from the archive, so they are not searchable text
    // either: a search finds what a reader can see (and the record's labels).
    const prose = hideHashtags(
      (note.content ?? '').replace(/nostr:[a-z0-9]+/gi, ' ').replace(/https?:\/\/\S+/gi, ' '),
    );
    const slugs = new Set(tags.map((t) => t.slug));
    // File each record under the broader terms it also belongs to (e.g. a
    // government record is also a Document). Only the filter index is widened;
    // the record's own tags, as shown on its card, are untouched.
    for (const s of [...slugs]) for (const extra of ALSO_FILED_UNDER[s] ?? []) slugs.add(extra);
    return {
      note,
      slugs,
      haystack: normalizeSearch(`${prose} ${tags.map((t) => t.label).join(' ')}`),
    };
  });
}

/** One membership rule for the feed, facet counts, and collection totals. */
function matchesCollection(n: IndexedNote, id: string | null): boolean {
  if (!id) return true;
  const collection = COLLECTIONS.find(c => c.id === id);
  if (!collection) return true;
  const assigned = RECORD_FILING_CODES[n.note.id];
  if (assigned !== undefined) return assigned.split(',').includes(FILING_CODES[id]);
  return collection.terms.some(slug => n.slugs.has(slug));
}

/**
 * Free-text match on normalized text. The whole query may match as one phrase
 * ("wtc 7"), or every word may match somewhere ("flight 77 pentagon").
 */
function matchesQuery(haystack: string, query: string): boolean {
  const whole = normalizeSearch(query);
  if (!whole) return true;
  if (alternatives(whole).some((a) => haystack.includes(a))) return true;
  const words = query.split(/\s+/).map(normalizeSearch).filter(Boolean);
  return words.every((w) => alternatives(w).some((a) => haystack.includes(a)));
}

/** Does a note satisfy the filters? Optionally ignore one facet's selections. */
function matches(n: IndexedNote, f: ArchiveFilters, skipFacet?: Facet): boolean {
  if (!matchesCollection(n, f.collection)) return false;

  const byFacet = new Map<Facet, string[]>();
  for (const slug of f.terms) {
    const facet = termBySlug(slug)?.facet;
    if (!facet || facet === skipFacet) continue;
    byFacet.set(facet, [...(byFacet.get(facet) ?? []), slug]);
  }
  for (const slugs of byFacet.values()) {
    if (!slugs.some((s) => n.slugs.has(s))) return false;
  }

  if (!matchesQuery(n.haystack, f.query)) return false;
  return true;
}

export function applyFilters(indexed: IndexedNote[], f: ArchiveFilters): NostrEvent[] {
  return indexed.filter((n) => matches(n, f)).map((n) => n.note);
}

/**
 * Per-term counts that respect the other active filters. A facet's own
 * selections are ignored when counting it, so choosing "Press" still shows how
 * many "Broadcast" records there are to add (the standard faceted behaviour).
 */
export function termCounts(indexed: IndexedNote[], f: ArchiveFilters): Record<string, number> {
  const counts: Record<string, number> = {};
  const facets = new Set<Facet>();
  for (const n of indexed) {
    for (const s of n.slugs) {
      const facet = termBySlug(s)?.facet;
      if (facet) facets.add(facet);
    }
  }
  for (const facet of facets) {
    for (const n of indexed) {
      if (!matches(n, f, facet)) continue;
      for (const s of n.slugs) {
        if (termBySlug(s)?.facet === facet) counts[s] = (counts[s] ?? 0) + 1;
      }
    }
  }
  return counts;
}

/** Exact record count per collection (a record counts once, however many of
 *  the collection's terms it carries). */
export function collectionCounts(indexed: IndexedNote[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of COLLECTIONS) {
    out[c.id] = indexed.filter((n) => matchesCollection(n, c.id)).length;
  }
  return out;
}
